import { type PartnerAccountResponse } from '@/api/routes/partners/partners.api';
import { getActiveLocale } from '@/i18n';
import { type PartnerInvitePreview } from '@/api/routes/public/partnerInvites.api';
import { type InviteView } from '@/domain/invite/types';
import { ScreenRoutes } from '@/types/router';
import { type User } from '@/types/user';

/**
 * The invite token survives the trip through sign-in, sign-up and the activation e-mail (which
 * opens in another tab) in the browser storage. A blocked storage only loses that convenience:
 * the sign-in link also carries `?invite=`.
 */
const PENDING_INVITE_KEY = 'bp_partners_pending_invite';

export const savePendingInvite = (token: string) => {
  try {
    window.localStorage.setItem(PENDING_INVITE_KEY, token);
  } catch {
    // Storage blocked (private mode, site data off): the query string still carries the token.
  }
};

export const readPendingInvite = (): string | null => {
  try {
    return window.localStorage.getItem(PENDING_INVITE_KEY);
  } catch {
    return null;
  }
};

export const clearPendingInvite = () => {
  try {
    window.localStorage.removeItem(PENDING_INVITE_KEY);
  } catch {
    // Nothing stored to clear.
  }
};

export const getInviteRoute = (token: string) =>
  ScreenRoutes.INVITE.replace(':token', encodeURIComponent(token));

/** `?invite=…&email=…` for the sign-in and sign-up links of the invite page. */
export const buildInviteAuthSearch = (token: string, email: string) =>
  `?${new URLSearchParams({ invite: token, email })}`;

/** Where sign-in leads: back to a pending invite, or home, where the account gate decides. */
export const getPostSignInRoute = (searchParams: URLSearchParams): string => {
  const token = searchParams.get('invite') || readPendingInvite();
  return token ? getInviteRoute(token) : ScreenRoutes.HOME;
};

/** E-mail prefilled on the auth screens when they come from an invite. */
export const getInviteEmail = (searchParams: URLSearchParams) =>
  searchParams.get('email')?.trim() ?? '';

const sameEmail = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/** What the invite page shows, before any accept attempt. */
export const getInviteView = ({
  invite,
  user,
  account,
}: {
  invite: Pick<PartnerInvitePreview, 'email'>;
  user?: Pick<User, 'email'> | null;
  account?: PartnerAccountResponse | null;
}): InviteView => {
  if (!user) return 'signed_out';
  if (account?.partner) return 'has_account';
  if (!sameEmail(user.email, invite.email)) return 'wrong_email';
  return 'ready';
};

/** The API answer to accept, as an invite view; `null` is an unexpected error. */
export const getAcceptErrorView = (status?: number): InviteView | null => {
  if (status === 403) return 'wrong_email';
  if (status === 404) return 'not_found';
  if (status === 409) return 'has_account';
  return null;
};

/** `25 out 2026` for "Vale até…". */
export const formatInviteDate = (iso: string) =>
  new Intl.DateTimeFormat(getActiveLocale(), { day: 'numeric', month: 'short', year: 'numeric' })
    .format(new Date(iso))
    .replace('.', '')
    .replace(/ de /g, ' ');
